import { useEffect, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";

/**
 * Animated graphic component that transitions when slide changes
 * Self-contained with height management to prevent layout shifts
 */
export function AnimatedGraphic({
  src,
  slideIndex,
}: {
  src: string;
  slideIndex: number;
}) {
  const [containerHeight, setContainerHeight] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Using the correct aspect ratio from viewBox="0 0 1014 576"
  // So the aspect ratio is 576/1014 = 0.568

  // Calculate and maintain stable height based on the correct aspect ratio
  useEffect(() => {
    const calculateHeight = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;

      // The correct aspect ratio (576/1014)
      const SVG_ASPECT_RATIO = 576 / 1014; // approx. 0.568

      // Calculate height based on container width and the correct SVG aspect ratio
      const calculatedHeight = containerWidth * SVG_ASPECT_RATIO;

      setContainerHeight(calculatedHeight);
    };

    // Debounced resize handler
    const debouncedResizeHandler = () => {
      // Clear any pending timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Set new timeout
      resizeTimeoutRef.current = setTimeout(() => {
        calculateHeight();
      }, 100); // 100ms debounce
    };

    // Calculate on mount
    calculateHeight();

    // Add debounced resize listener
    window.addEventListener("resize", debouncedResizeHandler);

    // Cleanup
    return () => {
      window.removeEventListener("resize", debouncedResizeHandler);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        height: containerHeight ? `${containerHeight}px` : "auto",
        minHeight: "300px",
        width: "100%",
        transition: "height 0.3s ease",
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={slideIndex}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
            mass: 0.8,
          }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={src} width="100%" />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
